import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { notifySessionExpired } from '../authSession';
import { DEV_MODE } from '../authConfig';
import { ensureMsalInitialized, msalInstance } from '../msalInstance';

const LEGACY_TOKEN_KEY = 'msal_access_token';
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

const apiScopes = [
  `api://${import.meta.env.VITE_AZURE_CLIENT_ID || 'cff246b3-7ce0-41fb-906c-484dde6116ed'}/access_as_user`,
];

let memoryCache: { token: string; expiresAt: number } | null = null;
let inFlight: Promise<string | null> | null = null;

function parseJwtExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isTokenFresh(token: string): boolean {
  const expiresAt = parseJwtExpiry(token);
  if (!expiresAt) return true;
  return Date.now() < expiresAt - EXPIRY_BUFFER_MS;
}

function clearLegacyStorage(): void {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/** Drop in-memory token cache (e.g. after 401). */
export function clearTokenCache(): void {
  memoryCache = null;
  clearLegacyStorage();
}

async function fetchToken(forceRefresh: boolean): Promise<string | null> {
  if (DEV_MODE) {
    return 'dev-token-12345';
  }

  clearLegacyStorage();
  await ensureMsalInitialized();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: apiScopes,
      account: accounts[0],
      forceRefresh,
    });
    const expiresAt = parseJwtExpiry(response.accessToken) ?? Date.now() + 55 * 60 * 1000;
    memoryCache = { token: response.accessToken, expiresAt };
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      console.warn('Interactive sign-in required to refresh API token');
      clearTokenCache();
      notifySessionExpired();
    } else {
      console.error('Failed to acquire API access token:', error);
    }
    return null;
  }
}

export interface GetTokenOptions {
  /** Bypass memory cache and ask MSAL for a new token. */
  forceRefresh?: boolean;
}

/**
 * Returns a valid API access token via the shared MSAL instance.
 * Concurrent callers share one in-flight refresh (single-flight).
 */
export async function getApiAccessToken(options?: GetTokenOptions): Promise<string | null> {
  const forceRefresh = options?.forceRefresh ?? false;

  if (!forceRefresh && memoryCache && isTokenFresh(memoryCache.token)) {
    return memoryCache.token;
  }

  if (inFlight && !forceRefresh) {
    return inFlight;
  }

  const promise = fetchToken(forceRefresh);
  inFlight = promise;
  try {
    return await promise;
  } finally {
    if (inFlight === promise) {
      inFlight = null;
    }
  }
}
